import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import {
  getUserCollections,
  createCollection as createCollectionApi,
  updateCollection as updateCollectionApi,
  deleteCollection as deleteCollectionApi,
  getCollectionItems,
  addItemToCollection,
  addItemsToCollectionBatch,
  removeItemFromCollection
} from '../services/api';

const CollectionContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useCollections = () => {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error('useCollections must be used within a CollectionProvider');
  }
  return context;
};

export const CollectionProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountCollectionProvider key={user?.id ?? 'guest'} user={user}>{children}</AccountCollectionProvider>;
};

const AccountCollectionProvider = ({ children, user }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCollections = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const data = await getUserCollections();
      setCollections(data || []);
    } catch (error) {
      console.error('Ошибка загрузки коллекций:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadCollections();
    } else {
      setCollections([]);
    }
  }, [user, loadCollections]);

  const createCollection = async (name) => {
    try {
      const newCollection = await createCollectionApi(name);
      setCollections(prev => [newCollection, ...prev]);
      return newCollection;
    } catch (error) {
      console.error('Ошибка создания коллекции:', error);
      throw error;
    }
  };

  const renameCollection = async (id, data) => {
    try {
      const updated = await updateCollectionApi(id, data);
      setCollections(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
      return updated;
    } catch (error) {
      console.error('Ошибка обновления коллекции:', error);
      throw error;
    }
  };

  const removeCollection = async (id) => {
    try {
      await deleteCollectionApi(id);
      setCollections(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Ошибка удаления коллекции:', error);
      throw error;
    }
  };

  const addToCollection = async (collectionId, shelfItemId) => {
    try {
      const result = await addItemToCollection(collectionId, shelfItemId);
      // Обновляем item_count и preview в коллекции
      setCollections(prev => prev.map(c => {
        if (c.id === collectionId) {
          return {
            ...c,
            item_count: Number(c.item_count || 0) + 1,
            preview_items: c.preview_items?.length < 3
              ? [...(c.preview_items || []), result]
              : c.preview_items
          };
        }
        return c;
      }));
      return result;
    } catch (error) {
      console.error('Ошибка добавления в коллекцию:', error);
      throw error;
    }
  };

  const addMultipleToCollection = async (collectionId, shelfItemIds) => {
    try {
      const result = await addItemsToCollectionBatch(collectionId, shelfItemIds);
      // Перезагружаем коллекции, чтобы получить актуальные preview
      await loadCollections();
      return result;
    } catch (error) {
      console.error('Ошибка массового добавления:', error);
      throw error;
    }
  };

  const removeFromCollection = async (collectionId, shelfItemId) => {
    try {
      await removeItemFromCollection(collectionId, shelfItemId);
      setCollections(prev => prev.map(c => {
        if (c.id === collectionId) {
          return {
            ...c,
            item_count: Math.max(0, (c.item_count || 0) - 1),
            preview_items: (c.preview_items || []).filter(i => i.id !== shelfItemId)
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Ошибка удаления из коллекции:', error);
      throw error;
    }
  };

  const fetchCollectionItems = async (collectionId) => {
    try {
      const items = await getCollectionItems(collectionId);
      return items || [];
    } catch (error) {
      console.error('Ошибка загрузки элементов коллекции:', error);
      return [];
    }
  };

  return (
    <CollectionContext.Provider value={{
      collections,
      loading,
      loadCollections,
      createCollection,
      renameCollection,
      removeCollection,
      addToCollection,
      addMultipleToCollection,
      removeFromCollection,
      fetchCollectionItems
    }}>
      {children}
    </CollectionContext.Provider>
  );
};

export default CollectionContext;